import { useMemo } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { usePinnedTrainingRuns } from '@apps/web/features/teach_phoebe/model/use_pinned_training_runs';
import { RunGroupList } from '@apps/web/features/teach_phoebe/ui/run_group_list';
import { groupRunsByScheduler } from '@apps/web/lib/training_run_utils';
import {
    useOrganizationTrainingNeedsReview,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export function NeedsReviewGroups({
    organizationId,
    onFeedbackSaved,
}: {
    readonly organizationId: string | null;
    readonly onFeedbackSaved?: () => void;
}) {
    const { data, isLoading, hasRequestFailed, mutate } =
        useOrganizationTrainingNeedsReview(
            organizationId,
            'teach-phoebe-needs-review',
        );
    const { data: currentUser } = useUser({
        reason: 'teach-phoebe-needs-review',
    });
    const { visibleRuns, handleReviewingChange, handleFeedbackSaved } =
        usePinnedTrainingRuns({
            runs: data?.runs ?? [],
            mutate,
            onFeedbackSaved,
        });
    const groups = useMemo(
        () => groupRunsByScheduler(visibleRuns, currentUser?.id),
        [currentUser?.id, visibleRuns],
    );

    if (isLoading) {
        return (
            <div className="space-y-3 py-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        );
    }
    if (hasRequestFailed && !data) {
        return (
            <div className="py-10 text-center">
                <p className="text-sm font-medium">
                    Couldn’t load recent activity
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    Something went wrong while loading the feed.
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => void mutate()}
                >
                    Try again
                </Button>
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="py-10 text-center">
                <p className="text-sm font-medium">All caught up</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    Nothing waiting for review. Phoebe keeps learning from
                    everything you teach.
                </p>
            </div>
        );
    }

    return (
        <RunGroupList
            groups={groups}
            organizationId={organizationId}
            onFeedbackSaved={handleFeedbackSaved}
            onReviewingChange={handleReviewingChange}
        />
    );
}
